import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { isDefined } from '@essent/core-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { InnogyComponentRendering } from '@core/jss-models';
import { getIsExperienceEditorActive } from '@core/jss-routing';
import { Store } from '@ngrx/store';
import type { FormGroupState } from 'ngrx-forms';
import type { Observable } from 'rxjs';
import { iif, of } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { NgrxFormsModule } from 'ngrx-forms';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TrackStepDirective } from '@analytics/events';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { TranslatePipe } from '@ngx-translate/core';

import {
  FormStep,
  selectFormStepShouldCollapse,
  selectIsStepReadonly,
} from '../+state';
import { finishCollapseStepAnimationAction } from '../+state/progressive-form-step';
import { ProgressiveFormBodyDirective } from '../progressive-form-body.directive';
import { ProgressiveFormSummaryDirective } from '../progressive-form-summary.directive';
import { ProgressiveFormService } from '../progressive-form.service';
import { ProgressiveFormStepCardComponent } from '../progressive-form-step-card/progressive-form-step-card.component';
import { ProgressiveFormSubmitButtonComponent } from '../progressive-form-submit-button/progressive-form-submit-button.component';

@Component({
  selector:
    'wl-progressive-form-step-wrapper[progressiveFormSelectorKey][formState]',
  templateUrl: './progressive-form-step-wrapper.component.html',
  host: { ngSkipHydration: 'true' },
  standalone: true,
  imports: [
    ProgressiveFormStepCardComponent,
    NgrxFormsModule,
    NgbCollapse,
    TrackStepDirective,
    NgTemplateOutlet,
    ProgressiveFormSubmitButtonComponent,
    AutomationIdDirective,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProgressiveFormStepWrapperComponent {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering: InnogyComponentRendering | undefined;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() title?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() subtitle?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formState?: FormGroupState<any> | null;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepNumber?: number;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() toolName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonText?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonAutomationId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonIcon?: IconProp;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() disableSubmitButton?: boolean | null;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() progressiveFormSelectorKey!: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showWhenFutureStep = true;
  /**
   * Deprecated: use useStepConfig and set the step config in the state using setFormConfig action (formSteps paramater)
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() isReadonly = false;

  //Techdebt: remove the need for useStepConfig when all progressive forms are using this instead of readonly.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() useStepConfig = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() step?: FormStep;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepKey!: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() hideSummary = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showSubmitButton = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showSeperator = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showProgression = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showWhenSkipped = false;

  @ContentChild(ProgressiveFormBodyDirective)
  formBody?: ProgressiveFormBodyDirective;
  @ContentChild(ProgressiveFormSummaryDirective)
  formSummary?: ProgressiveFormSummaryDirective;

  @Output() submitFormStep = new EventEmitter();

  constructor(
    private readonly store$: Store,
    private readonly progressiveFormService: ProgressiveFormService
  ) {}

  //Techdebt: remove this when all forms use config and create a proper Viewmodel instead of all these seperate observable methods
  isFormCollapsed$(): Observable<boolean> {
    if (this.useStepConfig) {
      return this.store$
        .select(selectFormStepShouldCollapse(this.stepKey))
        .pipe(filter(isDefined));
    }

    return this.isCurrentFormStep$.pipe(
      map((current) => !current || this.isReadonly)
    );
  }

  //Techdebt: remove this when all forms use config and create a proper Viewmodel instead of all these seperate observable methods
  isSummaryCollapsed$(): Observable<boolean> {
    if (this.useStepConfig) {
      return this.store$
        .select(selectFormStepShouldCollapse(this.stepKey))
        .pipe(
          filter(isDefined),
          map((step) => !step)
        );
    }

    return this.isCurrentFormStep$.pipe(
      map((current) => current && !this.isReadonly)
    );
  }
  //Techdebt: remove this when all forms use config and create a proper Viewmodel instead of all these seperate observable methods
  isReadonly$(): Observable<boolean> {
    if (this.useStepConfig) {
      return this.store$
        .select(selectIsStepReadonly(this.stepKey))
        .pipe(filter(isDefined));
    }

    return of(this.isReadonly);
  }

  get isInExperienceEditor$() {
    return this.store$.select(getIsExperienceEditorActive);
  }

  get isDisabled(): boolean {
    return this.disableSubmitButton || !!this.formState?.isDisabled;
  }

  get isValidAndSubmitted() {
    return this.formState?.isValid && this.formState?.isSubmitted;
  }

  get currentStepIndex$() {
    return this.progressiveFormService.currentStepPresentableNumber$(
      this.progressiveFormSelectorKey,
      this.formState?.id
    );
  }

  get totalSteps$() {
    return this.progressiveFormService.totalSteps$(
      this.progressiveFormSelectorKey
    );
  }

  get isSkipped$() {
    return this.progressiveFormService.isSkipped$(
      this.progressiveFormSelectorKey,
      this.formState?.id
    );
  }

  get isCurrentFormStep$(): Observable<boolean> {
    const isCurrentFormStep$ = this.progressiveFormService
      ?.currentActiveFormStepId$(this.progressiveFormSelectorKey)
      .pipe(map((formId) => this.formState?.id === formId));

    return this.isInExperienceEditor$.pipe(
      mergeMap((isInXp) => iif(() => isInXp, of(true), isCurrentFormStep$))
    );
  }

  get isCurrentEditedFormStep$(): Observable<boolean> {
    return this.progressiveFormService.isEditingFormStep$(
      this.progressiveFormSelectorKey,
      this.formState?.id
    );
  }

  get canBeEdited$(): Observable<boolean> | undefined {
    return this.progressiveFormService
      .isEditingPreviouslySubmittedForm$(this.progressiveFormSelectorKey)
      .pipe(
        map((isEditing = false) => !isEditing && !!this.formState?.isSubmitted)
      );
  }

  get isFutureFormStep$() {
    if (!this.formState) {
      return false;
    }
    return this.progressiveFormService?.isFutureStep$(
      this.progressiveFormSelectorKey,
      this.formState.id
    );
  }

  setEditingFormStep() {
    if (!this.formState) {
      return;
    }
    this.store$.dispatch(
      this.progressiveFormService.openFormStep({
        formId: this.progressiveFormSelectorKey,
        stepId: this.formState.id,
        stepFormState: this.formState,
        isEditing: true,
      })
    );
  }

  submitForm() {
    if (!this.formState) {
      return;
    }
    this.store$.dispatch(
      this.progressiveFormService.submitFormStep({
        formId: this.progressiveFormSelectorKey,
        formState: this.formState,
      })
    );
    this.submitFormStep.emit();
  }

  cancelEditingFormStep() {
    if (!this.formState) {
      return;
    }
    this.store$.dispatch(
      this.progressiveFormService.cancelEditingFormStep({
        formId: this.progressiveFormSelectorKey,
        stepId: this.formState.id,
      })
    );
  }

  formCollapseAnimationFinished(collapsed: boolean) {
    if (!this.formState) {
      return;
    }
    this.store$.dispatch(
      finishCollapseStepAnimationAction({
        formId: this.progressiveFormSelectorKey,
        stepId: this.formState?.id,
        collapsed,
      })
    );
  }
}
